/**
 * The AI Service detail page's Testing tab (task 800; Test runs since task 1177;
 * three sections since task 1185).
 *
 * With `innovate.fe.testing.sections` on (task 1185) the tab is a `SectionRail`
 * of **Tests**, **Criteria** and **Test runs** — the Agents and Developer Info
 * idiom — each with a counted summary line, so the tab says what to do next
 * ("Tests · None yet" reads as start here; tests with "Test runs · None yet"
 * reads as run them). The section is addressable as `?section=tests|criteria|runs`.
 * Generate tests is one button that hands the request to the Assistant sidebar;
 * the in-page generation flow is not on this layout. Off, the tab is the
 * single-scroll layout below, unchanged apart from the grading toggle (gone in
 * both layouts — every test run is graded).
 *
 * Not a rename of the old Test tab but a rework, per kv's 2026-08-31 ruling in
 * the source report: this tab shows two lists and no chat.
 *
 *   1. **Tests** — the persisted tests, with generation, Add test and Import as
 *      the ways to add more. Selecting tests and running them creates one
 *      **test run** (task 1176).
 *   2. **Test runs** — one row per test run (task 1177), replacing the generic
 *      collaboration run history this tab used to show. A test run's per-test
 *      rows are its **results**. The Advanced endpoint hub keeps the raw run
 *      history; this page shows what the tests found.
 *
 * **The interactive chat runner is deliberately absent.** Ad-hoc exercising of a
 * service is no longer this page's job; the Advanced endpoint hub keeps its own
 * Test tab and runner, so `EndpointRunner` is still very much alive — it is just
 * not what a service owner comes to the service page to do. That is also why
 * `TestsPanel` is rendered without `onLoadIntoRunner`: with no runner here, the
 * "load into runner" action would be a control that leads nowhere, so the panel
 * hides it rather than offering it.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowsRightLeftIcon, BeakerIcon, ClockIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { TestsPanel } from '@/components/endpoints/tests/TestsPanel';
import { GenerateTestsFlow, OpenGenerateTestsFlowButton } from '@/components/endpoints/tests/GenerateTestsFlow';
import { GenerateTestsButton } from '@/components/endpoints/tests/GenerateTestsButton';
import { TestRunsList } from '@/components/endpoints/tests/TestRunsList';
import { ServiceCriteriaSection } from '@/components/endpoints/tests/ServiceCriteriaSection';
import { CriteriaSection } from '@/components/endpoints/tests/CriteriaSection';
import { settledTests, TestCompareSection } from '@/components/endpoints/tests/TestCompareSection';
import { HowTestingWorksLink, TestingGuideLearnMore } from '@/components/endpoints/tests/TestingGuideLink';
import { SectionRail, type SectionRailEntry } from './SectionRail';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useEndpointTests } from '@/hooks/useEndpointTests';
import { useCriteria, useEndpointTestSuites } from '@/hooks/useEndpointTestEvals';
import { useEndpointTestRunGroups } from '@/hooks/useEndpointTestRunGroups';
import { formatRelativeTime } from '@/utils/dateUtils';
import type { Endpoint } from '@/types';

interface ServiceTestingTabProps {
  endpoint: Endpoint;
}

/** The sections the rail picks between, addressable as `?section=` (task 1185). */
export const TESTING_SECTIONS = ['tests', 'criteria', 'runs', 'compare'] as const;
export type TestingSection = (typeof TESTING_SECTIONS)[number];

/** Whether a raw `?section=` value names a section of this tab. */
export function isTestingSection(value: string | null | undefined): value is TestingSection {
  return !!value && (TESTING_SECTIONS as readonly string[]).includes(value);
}

/** Said once, above the Test runs list, when the service has no criteria (task 1185). */
export const NO_CRITERIA_RUNS_LINE =
  'No criteria yet, so results show whether the call completed. Add criteria for runs to pass or fail on their own, or mark results yourself in the result drawer.';

const NONE_YET = 'None yet';
const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function ServiceTestingTab({ endpoint }: ServiceTestingTabProps) {
  const sectionsEnabled = useFlag('innovate.fe.testing.sections');
  return sectionsEnabled ? (
    <ServiceTestingSections endpoint={endpoint} />
  ) : (
    <ServiceTestingSingleScroll endpoint={endpoint} />
  );
}

/**
 * A row's Compare (task 1227): the Compare section with that test as the
 * candidate (`b`); the section fills in the baseline from the suggestion.
 */
function useCompareNavigation() {
  const [, setSearchParams] = useSearchParams();
  return (b: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'testing');
      next.set('section', 'compare');
      next.set('b', b);
      next.delete('a');
      return next;
    });
}

/**
 * Compare is offered once the service has two or more settled tests, and only
 * while both of its levers are on: the test runs feature lever (task 1215) and
 * the comparison's own page flag (task 1216), which the old route reads too.
 */
function useCompareAvailable(settledCount: number) {
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  const comparePageEnabled = useFlag('innovate.fe.testing.compare.page');
  return runsEnabled && comparePageEnabled && settledCount >= 2;
}

/**
 * The three-section layout (task 1185). The summary lines read the same
 * queries the sections themselves render from (shared React Query keys), so a
 * test added, a criterion saved or a test run settling updates the rail with
 * no extra wiring.
 */
function ServiceTestingSections({ endpoint }: ServiceTestingTabProps) {
  // The criteria and test runs feature sets each have a lever (task 1215): off,
  // the section leaves the rail and a `?section=` naming it lands on Scenarios.
  const criteriaEnabled = useFlag('innovate.fe.testing.criteria');
  const runsEnabled = useFlag('innovate.fe.testing.runs');
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const [section, setSection] = useState<TestingSection>(
    isTestingSection(sectionParam) ? sectionParam : 'tests',
  );
  // `?testRun=` opens one test run in the Test runs section — where the
  // Assistant's Run these tests lands the User (task 1188).
  const testRunParam = searchParams.get('testRun');
  const [openTestRunId, setOpenTestRunId] = useState<string | null>(testRunParam);
  const handleCompare = useCompareNavigation();

  useEffect(() => {
    if (testRunParam) setOpenTestRunId(testRunParam);
  }, [testRunParam]);

  // A `?section=` arriving after mount (a link on this very page, or the
  // Assistant's Run these tests) moves the section; a rail click that wrote
  // the param re-runs this with the value it just set, which is a no-op.
  useEffect(() => {
    if (isTestingSection(sectionParam)) setSection(sectionParam);
  }, [sectionParam]);

  /** Move to a section and say so in the URL, so the section is shareable. */
  const selectSection = (id: TestingSection) => {
    setSection(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('section', id);
        return next;
      },
      { replace: true },
    );
  };

  const { data: testsData, isLoading: testsLoading } = useEndpointTests(endpoint.id);
  const { data: suites = [] } = useEndpointTestSuites(endpoint.id);
  const { data: criteria, isLoading: criteriaLoading } = useCriteria({ kind: 'service', endpointId: endpoint.id });
  const { data: runsData, isLoading: runsLoading } = useEndpointTestRunGroups(endpoint.id);

  const testCount = testsData?.pagination?.total ?? testsData?.data?.length ?? 0;
  const criteriaList = criteria ?? [];
  const runs = runsData?.data ?? [];
  const runCount = runsData?.pagination?.total ?? runs.length;
  const compareAvailable = useCompareAvailable(settledTests(runs).length);

  const testsSummary = testsLoading
    ? 'Loading…'
    : testCount === 0
      ? NONE_YET
      : [plural(testCount, 'scenario', 'scenarios'), suites.length > 0 ? plural(suites.length, 'suite', 'suites') : null]
          .filter(Boolean)
          .join(' · ');
  const criteriaSummary = criteriaLoading
    ? 'Loading…'
    : criteriaList.length === 0
      ? NONE_YET
      : `${plural(criteriaList.length, 'criterion', 'criteria')} · ${criteriaList.filter((c) => c.required).length} required`;
  const lastRunAt = runs[0]?.createdAt;
  const lastRunAgo = lastRunAt ? formatRelativeTime(lastRunAt) : null;
  const runsSummary = runsLoading
    ? 'Loading…'
    : runCount === 0
      ? NONE_YET
      : [plural(runCount, 'test', 'tests'), lastRunAgo && (lastRunAgo === 'just now' ? lastRunAgo : `last ${lastRunAgo}`)]
          .filter(Boolean)
          .join(' · ');

  const entries: SectionRailEntry[] = [
    { id: 'tests', label: 'Scenarios', summary: testsSummary, icon: BeakerIcon },
    ...(criteriaEnabled ? [{ id: 'criteria', label: 'Criteria', summary: criteriaSummary, icon: ScaleIcon }] : []),
    ...(runsEnabled ? [{ id: 'runs', label: 'Tests', summary: runsSummary, icon: ClockIcon }] : []),
    ...(compareAvailable ? [{ id: 'compare', label: 'Compare', summary: 'pick two tests', icon: ArrowsRightLeftIcon }] : []),
  ];
  const shownSection: TestingSection = entries.some((entry) => entry.id === section) ? section : 'tests';

  // A new test run lands the User on Test runs with it open, filling in.
  const handleTestRunCreated = (testRunId: string) => {
    setOpenTestRunId(testRunId);
    selectSection('runs');
  };

  return (
    <div className="flex flex-col gap-5 md:flex-row md:items-start" data-testid="service-testing-tab">
      {/* Below md the rail stacks above the section; at md and up it holds
          its width and sticks, as the Agents and Developer Info rails do. */}
      <div className="md:sticky md:top-4 md:w-[260px] md:shrink-0">
        <SectionRail
          heading="Testing"
          // Always visible, whatever section is open (task 1193), as a ? on
          // the heading row (task 1195).
          headingAction={<HowTestingWorksLink />}
          entries={entries}
          selectedId={shownSection}
          onSelect={(id) => selectSection(id as TestingSection)}
          testId="testing-rail"
          ariaLabel="Testing sections"
        />
      </div>

      <div className="min-w-0 flex-1">
        {shownSection === 'tests' && (
          <div data-testid="service-testing-scenarios">
            <TestsPanel
              endpoint={endpoint}
              onTestRunCreated={handleTestRunCreated}
              // The one Generate tests button: the empty-state card while there
              // are no tests, the header once there are — never both.
              renderGenerate={(placement) =>
                placement === 'header' && testCount === 0 ? null : <GenerateTestsButton placement={placement} />
              }
            />
          </div>
        )}

        {shownSection === 'criteria' && <CriteriaSection endpointId={endpoint.id} />}

        {shownSection === 'compare' && <TestCompareSection endpointId={endpoint.id} />}

        {shownSection === 'runs' && (
          <div className="space-y-3" data-testid="service-testing-runs">
            {/* Invites adding criteria, so it goes with the criteria lever. */}
            {criteriaEnabled && !criteriaLoading && criteriaList.length === 0 && (
              <p
                data-testid="testing-runs-no-criteria"
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
              >
                {NO_CRITERIA_RUNS_LINE}{' '}
                <TestingGuideLearnMore anchor="runs" testId="testing-runs-learn-more" />
              </p>
            )}
            <TestRunsList
              endpoint={endpoint}
              openId={openTestRunId}
              onOpenChange={setOpenTestRunId}
              onCompare={handleCompare}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The single-scroll layout, the flag-off fallback (tasks 800-1180): criteria,
 * tests (or the in-page generation flow), then test runs.
 */
function ServiceTestingSingleScroll({ endpoint }: ServiceTestingTabProps) {
  const [generateOpen, setGenerateOpen] = useState(false);
  const [openTestRunId, setOpenTestRunId] = useState<string | null>(null);
  const runsRef = useRef<HTMLDivElement>(null);
  const handleCompare = useCompareNavigation();
  // This layout has no rail; a row's Compare (or the old compare URL) opens
  // the Compare section below the Tests list instead (task 1227).
  const [searchParams] = useSearchParams();
  const { data: runsData } = useEndpointTestRunGroups(endpoint.id);
  const compareAvailable = useCompareAvailable(settledTests(runsData?.data ?? []).length);
  const compareOpen = compareAvailable && searchParams.get('section') === 'compare';

  // A new test run opens at the top of the list, scrolled into view, so the
  // owner watches it fill in (task 1176).
  const handleTestRunCreated = (testRunId: string) => {
    setOpenTestRunId(testRunId);
    runsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6" data-testid="service-testing-tab">
      {/* The standing rules first (task 1180): every result is measured against them. */}
      <ServiceCriteriaSection endpointId={endpoint.id} />

      {/* Tests first: a test run only means something once there are saved
          tests that produced it. */}
      <div data-testid="service-testing-scenarios">
        {generateOpen ? (
          <GenerateTestsFlow endpoint={endpoint} onClose={() => setGenerateOpen(false)} />
        ) : (
          <TestsPanel
            endpoint={endpoint}
            renderGenerate={(placement) => (
              <OpenGenerateTestsFlowButton placement={placement} onOpen={() => setGenerateOpen(true)} />
            )}
            onTestRunCreated={handleTestRunCreated}
          />
        )}
      </div>

      <div data-testid="service-testing-runs" ref={runsRef}>
        <TestRunsList
          endpoint={endpoint}
          openId={openTestRunId}
          onOpenChange={setOpenTestRunId}
          onCompare={handleCompare}
        />
      </div>

      {compareOpen && <TestCompareSection endpointId={endpoint.id} />}
    </div>
  );
}

export default ServiceTestingTab;
